<template>
  <v-data-table
    class="rmr-data-table gw-table"
    item-key="id"
    :headers="headers"
    :items="items"
    :loading="loading"
    :expanded="expandedItems"
    :single-expand="false"
    :options="options"
    :server-items-length="total"
    :item-class="rowClass"
    disable-filtering
    disable-sort
    :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
    @update:options="$emit('update:options', $event)"
  >
    <!-- Explicit column rather than `show-select`: Vuetify's own selection is
         page-local and resets whenever `items` changes. -->
    <template v-slot:item.select="{ item }">
      <v-simple-checkbox
        :value="isSelected(item.id)"
        :disabled="item.used"
        :ripple="false"
        color="primary"
        @input="$emit('toggle-select', item)"
      />
    </template>

    <template v-slot:item.title="{ item }">
      <div class="gw-table__title-cell" @click="$emit('toggle-expand', item)">
        <img
          v-if="item.preview && !failedImages[item.id]"
          :src="item.preview"
          alt=""
          class="gw-table__thumb"
          @error="onImageError(item.id)"
        />
        <div v-else class="gw-table__thumb gw-table__thumb--placeholder">
          {{ initialOf(item) }}
        </div>

        <span class="gw-table__title" :title="item.title">{{ item.title }}</span>

        <span class="gw-table__caret">
          <v-icon size="14" color="#94A3B8">
            {{ isExpanded(item.id) ? icons.mdiChevronUp : icons.mdiChevronDown }}
          </v-icon>
        </span>
      </div>
    </template>

    <template v-slot:item.domain="{ item }">
      <span class="gw-table__domain">{{ item.domain || "—" }}</span>
    </template>

    <template v-slot:item.sourceName="{ item }">
      <source-type-badge :source-name="item.sourceName" />
    </template>

    <!-- Une source vidéo est hors scoring : on affiche un tiret plutôt qu'un
         « 0.0 » qui se lirait comme un mauvais score. -->
    <template v-slot:item.score="{ item }">
      <score-pill v-if="item.score !== null" :score="item.score" :notes="item.notes" />
      <span v-else class="gw-table__no-score">—</span>
    </template>

    <template v-slot:item.publicationDate="{ item }">
      <span class="gw-table__date" :title="item.publicationDate">
        {{ relativeDate(item.publicationDate) }}
      </span>
    </template>

    <template v-slot:item.link="{ item }">
      <a
        v-if="item.url"
        :href="item.url"
        target="_blank"
        rel="noopener"
        class="gw-table__link"
      >
        Ouvrir
        <v-icon size="13" color="currentColor">{{ icons.mdiOpenInNew }}</v-icon>
      </a>
    </template>

    <template v-slot:expanded-item="{ headers: cols, item }">
      <td :colspan="cols.length" class="gw-table__detail-cell">
        <source-detail-panel
          :item="item"
          layout="row"
          @open-video="$emit('open-video', $event)"
        />
      </td>
    </template>

    <template v-slot:no-data>
      <div class="rmr-data-table__state">
        <div class="rmr-data-table__state-title">Aucun résultat</div>
        <div v-if="isVideo" class="rmr-data-table__state-text">
          Élargissez la période, ou vérifiez qu'au moins une config de ce site
          est déclarée en vidéo.
        </div>
        <div v-else class="rmr-data-table__state-text">
          Élargissez la période, baissez le score minimum ou décochez
          « Masquer les déjà traités ».
        </div>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import { computed, reactive, set } from "@vue/composition-api";
import { mdiChevronDown, mdiChevronUp, mdiOpenInNew } from "@mdi/js";

import ScorePill from "./ScorePill.vue";
import SourceDetailPanel from "./SourceDetailPanel.vue";
import SourceTypeBadge from "./SourceTypeBadge.vue";
import { formatRelativeDate } from "../source-presentation";

// All columns are unsortable on purpose: the API only understands
// `sort_by=score`, and anything else falls back to date desc without saying so.
// Sorting is driven by the toolbar's segmented control instead.
const HEADERS = [
  { text: "", value: "select", width: "48px", sortable: false },
  { text: "Titre", value: "title", sortable: false },
  { text: "Domaine", value: "domain", width: "160px", sortable: false },
  { text: "Source", value: "sourceName", width: "170px", sortable: false },
  { text: "Score", value: "score", width: "90px", sortable: false },
  { text: "Publication", value: "publicationDate", width: "130px", sortable: false },
  { text: "", value: "link", width: "100px", sortable: false },
];

// Les sources vidéo sont hors scoring, et leur domaine est la plateforme
// (youtube.com partout) : les deux colonnes seraient vides de bout en bout.
const VIDEO_HIDDEN_COLUMNS = ["score", "domain"];
const VIDEO_HEADERS = HEADERS.filter(
  (header) => !VIDEO_HIDDEN_COLUMNS.includes(header.value)
);

export default {
  name: "SourcesTableView",
  components: {
    ScorePill,
    SourceDetailPanel,
    SourceTypeBadge,
  },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    options: { type: Object, required: true },
    total: { type: Number, default: 0 },
    expandedItems: { type: Array, default: () => [] },
    isSelected: { type: Function, required: true },
    isExpanded: { type: Function, required: true },
    isVideo: { type: Boolean, default: false },
  },
  setup(props) {
    const failedImages = reactive({});

    const onImageError = (id) => {
      // Marfeel, Google News and Perplexity rows carry no preview at all.
      set(failedImages, id, true);
    };

    const rowClass = (item) => {
      const classes = [];
      if (item.used) classes.push("gw-row--used");
      if (props.isSelected(item.id)) classes.push("gw-row--selected");
      return classes.join(" ");
    };

    const initialOf = (item) =>
      (item.domain || item.title || "?").charAt(0).toUpperCase();

    return {
      failedImages,
      headers: computed(() => (props.isVideo ? VIDEO_HEADERS : HEADERS)),
      icons: { mdiChevronDown, mdiChevronUp, mdiOpenInNew },
      initialOf,
      onImageError,
      relativeDate: formatRelativeDate,
      rowClass,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

// Compact density — the design brief's default.
.gw-table ::v-deep tbody td {
  padding: 8px 16px !important;
}

.gw-table ::v-deep .v-data-table-header th {
  padding: 10px 16px !important;
}

.gw-table ::v-deep .gw-row--used {
  opacity: 0.55;
}

// `!important` is required: `.rmr-data-table tbody tr:hover` already carries one.
.gw-table ::v-deep .gw-row--selected td {
  background: $rmr-indigo-bg !important;
}

.gw-table__title-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 320px;
  cursor: pointer;

  &:hover .gw-table__title {
    color: $rmr-indigo;
  }
}

.gw-table__thumb {
  flex-shrink: 0;
  width: 64px;
  height: 44px;
  border-radius: $rmr-radius-xs;
  object-fit: cover;
  background: $rmr-surface-2;
}

.gw-table__thumb--placeholder {
  font-size: 15px;
  @include gw-thumb-placeholder;
}

.gw-table__title {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  line-height: 1.3;
  color: $rmr-text-base;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gw-table__caret {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-pill;
  background: $rmr-surface-0;
}

.gw-table__domain {
  color: $rmr-text-muted;
  font-size: 12.5px;
}

.gw-table__no-score {
  color: $rmr-text-faint;
  font-size: 13px;
}

.gw-table__date {
  color: $rmr-text-subtle;
  font-size: 12.5px;
  white-space: nowrap;
}

.gw-table__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $rmr-indigo;
  font-weight: 600;
  font-size: 12.5px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.gw-table__detail-cell {
  padding: 4px 16px 16px 62px !important;
  background: $rmr-surface-0;
}
</style>
